import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';

// Actions
import ReviewsActions from 'app/shared/actions/ReviewsActions';
import ZillowSubscriptionActions from 'app/shared/actions/ZillowSubscriptionActions';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import Constants from 'app/shared/constants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ZillowSubscriptionConstants from 'app/shared/constants/ZillowSubscriptionConstants';

import {
    Checkbox,
    Label,
    LabeledControl,
    LabeledRatingStars,
    RatingStars,
    Select,
    Spacer,
    Text,
} from '@zillow/constellation';
import { Col, Container, Row } from '@zillow/zrs-comet';

import { RMWContext } from 'app/shared/providers/RMWContext';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import Review from 'app/shared/components/Review';
import ReviewResponse from 'app/shared/components/ReviewResponse';
import PDPContentSkeleton from 'app/shared/components/skeletons/PDPContentSkeleton';
import SearchBox from 'app/shared/components/SearchBox';

// Selectors
import { UserSelectors, ReviewsSelectors } from 'app/shared/selectors';

// Stylesheets
import './style.scss';

const { startCaseRemoveSpace } = stringHelper;
const SORT_VALUE_MAP = {
    CREATED: 'created',
    RATING: 'rating',
};

const propTypes = {
    match: PropTypes.object.isRequired,
    reviewsForListing: PropTypes.object.isRequired,
};

class ReviewsPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = {
            searchText: '',
            searchedOnce: false,
            hideReviewsWithResponses: false,
            sort: SORT_VALUE_MAP.CREATED,
            isLoadingReviews: true,
            isLoadingReviewNotifications: true,
            error: '',
        };

        this.handleSeeMore = this.handleSeeMore.bind(this);
        this.handleSubmitResponse = this.handleSubmitResponse.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleHideReviewsWithResponsesToggle = this.handleHideReviewsWithResponsesToggle.bind(this);
        this.handleReviewNotificationToggle = this.handleReviewNotificationToggle.bind(this);
        this.handleReportReview = this.handleReportReview.bind(this);
        this.handleDeleteResponse = this.handleDeleteResponse.bind(this);
        this.getReviews = this.getReviews.bind(this);
        this.getReviewNotifications = this.getReviewNotifications.bind(this);
        this.getReviewZillowSubscriptionIsEmailable = this.getReviewZillowSubscriptionIsEmailable.bind(this);
        this.renderHeader = this.renderHeader.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this.renderError = this.renderError.bind(this);
    }

    componentDidMount() {
        this.getReviews();
        this.getReviewNotifications();
    }

    handleSearch(searchText) {
        const { searchedOnce } = this.state;
        const { dispatch } = this.props;
        const newState = { searchText };
        if (!searchedOnce) {
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.SEARCH_REVIEWS));
            newState.searchedOnce = true;
        }
        this.setState(newState, this.getReviews);
    }

    handleSortChange(e) {
        const { dispatch } = this.props;
        const sort = e.target.value;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.SORT_REVIEWS, {
            label: startCaseRemoveSpace(sort),
        }));
        this.setState({ sort }, this.getReviews);
    }

    handleHideReviewsWithResponsesToggle() {
        const { hideReviewsWithResponses } = this.state;
        const { dispatch } = this.props;
        const newHideReviewsWithResponses = !hideReviewsWithResponses;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.HIDE_REVIEWS, {
            label: startCaseRemoveSpace(newHideReviewsWithResponses.toString()),
        }));
        this.setState({ hideReviewsWithResponses: newHideReviewsWithResponses }, this.getReviews);
    }

    handleReviewNotificationToggle() {
        const { dispatch } = this.props;
        const reviewSubscriptionSubscribed = this.getReviewZillowSubscriptionIsEmailable();
        const action = reviewSubscriptionSubscribed ? 'stop' : 'start';
        // eslint-disable-next-line no-alert
        if (confirm(`You'll ${action} receiving notifications for ALL your properties. Do you want to continue?`)) {
            this.setState({ isLoadingReviewNotifications: true });
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.NOTIFICATION_REVIEWS, {
                label: startCaseRemoveSpace(!reviewSubscriptionSubscribed.toString()),
            }));
            return dispatch(ZillowSubscriptionActions.updateSubscriptionStatus(
                ZillowSubscriptionConstants.ZRM_REVIEWS.name, !reviewSubscriptionSubscribed
            ))
                .then(() => this.setState({ isLoadingReviewNotifications: false }))
                .catch(() => this.setState({ isLoadingReviewNotifications: false }));
        }
        return false;
    }

    handleSubmitResponse(reviewId, reviewType, message, reviewResponseId = '') {
        const { dispatch } = this.props;
        const params = {
            reviewId,
            reviewType,
            message,
        };
        if (reviewResponseId !== '') {
            params.reviewResponseId = reviewResponseId;
            return dispatch(ReviewsActions.updateResponse(params));
        }
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.CREATE_REVIEW_RESPONSE));
        return dispatch(ReviewsActions.createResponse(params));
    }

    handleReportReview(reviewId, reviewType, reason, comment = '') {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.REPORT_REVIEW, {
            label: reason,
        }));
        return dispatch(ReviewsActions.reportReview({
            reviewId, reviewType, reason, comment,
        }));
    }

    handleDeleteResponse(reviewResponseId, reviewType) {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DELETE_REVIEW_RESPONSE));
        return dispatch(ReviewsActions.deleteResponse({ reviewResponseId, reviewType }));
    }

    handleSeeMore() {
        const { reviewsForListing } = this.props;
        const { totalNumberOfReviews } = reviewsForListing;
        const nbrReviews = reviewsForListing.reviews.length;

        // if on last page
        if (nbrReviews >= totalNumberOfReviews) {
            return;
        }

        this.getReviews(nbrReviews, true);
    }

    getReviews(start = 0, appendToReviews = false) {
        const {
            sort,
            hideReviewsWithResponses,
            isLoadingReviews,
            searchText,
        } = this.state;
        const { dispatch, match } = this.props;
        const params = {
            listingId: match.params.propertyId,
            limit: 10,
            start,
            searchText,
            sort,
            hideReviewsWithResponses,
        };

        if (!isLoadingReviews) {
            this.setState({ isLoadingReviews: true });
        }

        return dispatch(ReviewsActions.getReviewsForListing(params, appendToReviews))
            .then((result) => {
                if (result.success === false) {
                    this.setState({
                        isLoadingReviews: false,
                        error: ErrorMessageConstants.DEFAULT_ERROR,
                    });
                } else {
                    this.setState({
                        isLoadingReviews: false,
                        error: '',
                    });
                }
            }).catch(() => {
                this.setState({
                    isLoadingReviews: false,
                    error: ErrorMessageConstants.DEFAULT_ERROR,
                });
            });
    }

    getReviewNotifications() {
        const { dispatch } = this.props;
        this.setState({ isLoadingReviewNotifications: true });
        return dispatch(ZillowSubscriptionActions.getSubscriptionStatus(ZillowSubscriptionConstants.ZRM_REVIEWS.name))
            .then(() => this.setState({ isLoadingReviewNotifications: false }))
            .catch(() => this.setState({ isLoadingReviewNotifications: false }));
    }

    getTotalText(totalNumberOfReviews, averageStarLevel) {
        let text = 'No Review';
        const hasReviews = totalNumberOfReviews > 0;
        if (hasReviews) {
            const textItems = [
                stringHelper.pluralize(totalNumberOfReviews, 'review', 'reviews'),
            ];

            if (averageStarLevel > 0) {
                textItems.push(`${averageStarLevel.toFixed(1)} average`);
            }

            text = `(${textItems.join(', ')})`;
        }

        return <Text fontType="body" fontColor="gray400" uppercase={!hasReviews}>{text}</Text>;
    }

    getReviewZillowSubscriptionIsEmailable() {
        const { zillowSubscriptionStatusesByType } = this.props;
        return zillowSubscriptionStatusesByType[ZillowSubscriptionConstants.ZRM_REVIEWS.persistentString]?.isEmailable;
    }

    renderHeader() {
        const { brand } = this.context;
        const {
            isLoadingReviews, isLoadingReviewNotifications, sort, hideReviewsWithResponses,
        } = this.state;
        const { reviewsForListing } = this.props;
        const { totalNumberOfReviews, averageStarLevel } = reviewsForListing;
        const reviewIsEmailable = this.getReviewZillowSubscriptionIsEmailable();

        return (
            <div>
                <Row>
                    <Col>
                        <Text className="ReviewsPage-title" fontType="h4">Reviews</Text>
                        {!isLoadingReviews && (
                            <LabeledRatingStars
                                ratingStars={(
                                    <RatingStars
                                        value={totalNumberOfReviews ? averageStarLevel : null}
                                        aria-label={`${averageStarLevel} out of 5 stars`}
                                    />
                                )}
                                label={this.getTotalText(totalNumberOfReviews, averageStarLevel)}
                            />
                        )}
                    </Col>
                </Row>
                {brand === Constants.UNFORMATTED_BRAND_NAMES.zillow && (
                    <Row noPadding={true}>
                        <Col>
                            <LabeledControl
                                label={<Label>Notify me when new reviews are posted</Label>}
                                disabled={isLoadingReviewNotifications}
                                control={(
                                    <Checkbox
                                        checked={reviewIsEmailable}
                                        onChange={this.handleReviewNotificationToggle}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col lg={12} xl={3}>
                        <SearchBox
                            placeholder="Search reviews"
                            onSearch={this.handleSearch}
                        />
                    </Col>
                    <Col lg={12} xl={6}>
                        <Spacer>
                            <LabeledControl
                                name="hide-responses"
                                label={<Label>Hide reviews with response</Label>}
                                control={(
                                    <Checkbox
                                        checked={hideReviewsWithResponses}
                                        onChange={this.handleHideReviewsWithResponsesToggle}
                                    />
                                )}
                            />
                        </Spacer>
                    </Col>
                    <Col lg={12} xl={3}>
                        <Select
                            name="sort"
                            value={sort}
                            onChange={this.handleSortChange}
                            disabled={isLoadingReviewNotifications}
                        >
                            <option value={SORT_VALUE_MAP.CREATED}>Date (new)</option>
                            <option value={SORT_VALUE_MAP.RATING}>Rating (highest)</option>
                        </Select>
                    </Col>
                </Row>
            </div>
        );
    }

    renderError() {
        const { reviewsForListing } = this.props;
        const { error } = this.state;
        let message;
        if (error) {
            message = error;
        } else if (reviewsForListing.reviews.length === 0) {
            message = 'There were no reviews found for this listing.';
        }

        return message ? <Spacer paddingTop="md"><Text fontColor="red400">{message}</Text></Spacer> : null;
    }

    renderContent() {
        const { user, reviewsForListing } = this.props;
        const canRespondToReviews = user.features.canRespondToReview;
        return (
            <div>
                {this.renderError()}
                {reviewsForListing.reviews.map(review => (
                    <Spacer key={`${review.reviewType}-${review.id}`} paddingTop="md">
                        <Review
                            review={review}
                            onSubmitResponse={this.handleSubmitResponse}
                            onReportReview={this.handleReportReview}
                            canEdit={canRespondToReviews}
                        />
                        {review.responseMessage && (
                            <ReviewResponse
                                review={review}
                                onSubmitResponse={this.handleSubmitResponse}
                                onDeleteResponse={this.handleDeleteResponse}
                                canEdit={canRespondToReviews}
                            />
                        )}
                    </Spacer>
                ))}
                {reviewsForListing.reviews.length > 0 &&
                    <VisibilitySensor onChange={this.handleSeeMore} />
                }
            </div>
        );
    }

    render() {
        const { isLoadingReviews } = this.state;
        return (
            <Spacer className="ReviewsPage" paddingBottom="md">
                <HeadWrapper title="Reviews" />
                <Container>
                    {this.renderHeader()}
                    {!isLoadingReviews && this.renderContent()}
                </Container>
                {isLoadingReviews && <PDPContentSkeleton />}
            </Spacer>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    reviewsForListing: ReviewsSelectors.makeReviewsForListing(state),
    zillowSubscriptionStatusesByType: state.zillowSubscription.statusesByType,
});

ReviewsPage.propTypes = propTypes;

export { ReviewsPage };
export default connect(mapStateToProps)(ReviewsPage);
